import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";

/**
 * IconGridCta — the closing-CTA layout shared by the home (industries) and the
 * Project page: a 9×7 grid of faint tiles with scattered white icon cards
 * framing a centred headline on desktop, collapsing to a compact icon wrap above
 * the copy on mobile. Content-agnostic — icons, eyebrow, title, body and the CTA
 * actions all arrive via props. Wrap it in a page-level <section> for the
 * spacing / rounding that page needs.
 *
 * Replaces the two hand-copied versions that previously lived in IndustryCta and
 * ProjectManagement (identical ICON_AT / HOLE grid math).
 */

export type CtaGridIcon = { name: string; Icon: LucideIcon; color: string };

// 9 cols × 7 rows. Scattered icon cards around an empty centre (the headline
// hole). Both callers pass exactly 13 icons (indices 0–12).
const ICON_AT: Record<number, number> = {
  1: 0, 5: 2, 11: 9, 16: 4, 18: 6, 26: 8, 37: 5, 43: 1, 45: 10, 49: 11, 53: 12, 56: 7, 60: 3,
};
const HOLE = new Set([20, 21, 22, 23, 24, 29, 30, 31, 32, 33, 38, 39, 40, 41, 42]);
const CELLS = 9 * 7;

function IconCard({ item }: { item: CtaGridIcon }) {
  const { Icon, color, name } = item;
  return (
    <div className="aspect-square rounded-2xl bg-white p-2 shadow-[0_10px_30px_rgba(15,23,42,0.12)]" title={name}>
      <span className="grid h-full w-full place-items-center rounded-xl bg-bg-subtle">
        <Icon size={30} strokeWidth={2.2} style={{ color }} />
      </span>
    </div>
  );
}

export function IconGridCta({
  items,
  eyebrow,
  title,
  body,
  actions,
}: {
  items: CtaGridIcon[];
  eyebrow: ReactNode;
  title: ReactNode;
  body: ReactNode;
  actions: ReactNode;
}) {
  return (
    <div className="relative mx-auto max-w-content px-4 py-16 md:px-10 md:py-20">
      {/* DESKTOP — decorative icon grid framing the headline; centre cells empty */}
      <div className="hidden lg:block" aria-hidden>
        <div className="grid grid-cols-9 gap-3">
          {Array.from({ length: CELLS }).map((_, i) =>
            HOLE.has(i)
              ? <div key={i} className="aspect-square" />
              : ICON_AT[i] !== undefined
                ? <IconCard key={i} item={items[ICON_AT[i]]} />
                : <div key={i} className="aspect-square rounded-2xl bg-ds-tint" />,
          )}
        </div>
      </div>

      {/* Single headline — overlaid on the grid (lg) / in flow (mobile). Rendered
          once so the title + copy never duplicate across breakpoints. */}
      <div className="lg:absolute lg:inset-0 lg:flex lg:items-center lg:justify-center lg:px-6">
        <div className="mx-auto flex max-w-lg flex-col items-center gap-5 text-center">
          {eyebrow}
          <h2 className="text-ds-38 font-semibold leading-[1.08] tracking-[-1px] text-ds-ink sm:text-ds-46">{title}</h2>
          <p className="text-ds-18 leading-relaxed text-ds-ink-soft">{body}</p>
          {actions}
        </div>
      </div>

      {/* MOBILE / TABLET — compact wrap of icon tiles under the headline */}
      <div className="mx-auto mt-10 flex max-w-sm flex-wrap justify-center gap-3 lg:hidden">
        {items.map((item) => (
          <span key={item.name} className="grid h-14 w-14 place-items-center rounded-2xl bg-white p-2 shadow-[0_8px_24px_rgba(15,23,42,0.12)]" title={item.name}>
            <span className="grid h-full w-full place-items-center rounded-lg bg-bg-subtle">
              <item.Icon size={20} strokeWidth={2.2} style={{ color: item.color }} />
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}
